import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiGetCookieConsent, apiUpdateCookieConsent } from '../services/api';

type CookieConsentState = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  resolved: boolean;
};

type CookieConsentContextType = {
  consent: CookieConsentState;
  canUseNonEssentialCookies: boolean;
  acceptAll: () => Promise<void>;
  acceptEssentialOnly: () => Promise<void>;
  updateConsent: (next: { analytics: boolean; marketing: boolean }) => Promise<void>;
  openSettingsDialog: () => void;
  closeSettingsDialog: () => void;
  settingsOpen: boolean;
};

const STORAGE_KEY = 'dragonsframe_cookie_consent_v1';

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

function readLocalConsent(): CookieConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { essential: true, analytics: false, marketing: false, resolved: false };
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: Boolean(parsed?.analytics),
      marketing: Boolean(parsed?.marketing),
      resolved: Boolean(parsed?.resolved),
    };
  } catch {
    return { essential: true, analytics: false, marketing: false, resolved: false };
  }
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [consent, setConsent] = useState<CookieConsentState>(() => readLocalConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  const persist = (next: CookieConsentState) => {
    setConsent(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const syncServer = useCallback(async (next: { analytics: boolean; marketing: boolean }) => {
    if (!user?.id) return;
    await apiUpdateCookieConsent(next);
  }, [user?.id]);

  const updateConsent = useCallback(async (next: { analytics: boolean; marketing: boolean }) => {
    const state: CookieConsentState = {
      essential: true,
      analytics: next.analytics,
      marketing: next.marketing,
      resolved: true,
    };
    persist(state);
    await syncServer(next);
  }, [syncServer]);

  const acceptAll = useCallback(async () => updateConsent({ analytics: true, marketing: true }), [updateConsent]);
  const acceptEssentialOnly = useCallback(async () => updateConsent({ analytics: false, marketing: false }), [updateConsent]);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const serverConsent = await apiGetCookieConsent();
        if (cancelled) return;
        persist({
          essential: true,
          analytics: Boolean(serverConsent.analytics),
          marketing: Boolean(serverConsent.marketing),
          resolved: true,
        });
      } catch {
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const value = useMemo<CookieConsentContextType>(() => ({
    consent,
    canUseNonEssentialCookies: consent.resolved && (consent.analytics || consent.marketing),
    acceptAll,
    acceptEssentialOnly,
    updateConsent,
    settingsOpen,
    openSettingsDialog: () => setSettingsOpen(true),
    closeSettingsDialog: () => setSettingsOpen(false),
  }), [acceptAll, acceptEssentialOnly, consent, settingsOpen, updateConsent]);

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export function useCookieConsent(): CookieConsentContextType {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
}
